import {
  ChatRenderer,
  ChatRendererProvider,
  createToolRegistry,
  GenericToolCall,
  GetOperatorTool,
  GetShopProductTool,
  OperatorResultTool,
  RagResultTool,
  RecommendShopProductsTool,
  canRenderClientSideForm,
  renderClientSideFormFallback,
  type ToolComponentProps,
  UsageLimitsErrorTool,
} from '@kanbu/chatbot-ui';
import type { ChatMessageType, ToolCall } from '@kanbu/schema';
import { ToolNames } from '@kanbu/schema/contracts';
import { SourcesDisplayMode, SourcesFormat } from '@kanbu/schema/enums';
import { useMemo } from 'react';

import { AdminFeedback } from './AdminFeedback';
import { AdminSourceDetails } from './AdminSourceDetails';
import { AdminToolCallDetails } from './AdminToolCallDetails';
import { mergeToolResultMessages } from './mergeToolResultMessages';

/**
 * Wraps a registered tool component so the admin inbox shows a collapsed
 * "call details" inspector (arguments, result, artifact) under it.
 */
function withToolInspector<T extends { component: React.ComponentType<any> }>(
  toolDef: T,
): T {
  const Original = toolDef.component;

  return {
    ...toolDef,
    component: (props: ToolComponentProps) => (
      <div className='flex w-full flex-col gap-1'>
        <Original {...props} />
        <AdminToolCallDetails {...props} />
      </div>
    ),
  };
}

export interface SourceCitationConfig {
  sourcesFormat?: SourcesFormat;
  sourcesDisplayMode?: SourcesDisplayMode;
  showToolActivity?: boolean;
  expandToolActivity?: boolean;
  inlineCitations?: boolean;
}

export interface AdminChatRendererProps {
  messages: ChatMessageType[];
  agentName?: string;
  agentAvatar?: string;
  chatbotConfig?: SourceCitationConfig;
  /**
   * Debug view: render every tool call through the full inspector (arguments,
   * result, artifact) instead of the customer activity trail. Off by default —
   * the inbox exposes a toggle for it.
   */
  debug?: boolean;
  isVoice?: boolean;
}

export function AdminChatRenderer({
  messages,
  agentName,
  agentAvatar,
  chatbotConfig,
  debug = false,
  isVoice = false,
}: AdminChatRendererProps) {
  const sourcesFormat = chatbotConfig?.sourcesFormat ?? SourcesFormat.Simple;
  const sourcesDisplayMode =
    chatbotConfig?.sourcesDisplayMode ?? SourcesDisplayMode.Pills;
  const inlineCitations = chatbotConfig?.inlineCitations ?? false;
  const transcriptMessages = useMemo(
    () => mergeToolResultMessages(messages),
    [messages],
  );

  const toolRegistry = useMemo(() => {
    const registry = createToolRegistry({
      // v1 rows carried the answer inside these synthetic tool calls.
      [ToolNames.RagResult]: RagResultTool,
      [ToolNames.RagResultOperator]: OperatorResultTool,
      [ToolNames.RecommendShopProducts]: RecommendShopProductsTool,
      [ToolNames.ShowShopProductDetail]: GetShopProductTool,
      [ToolNames.UsageLimitsError]: UsageLimitsErrorTool,
      [ToolNames.GetOperator]: GetOperatorTool,
    });

    if (!debug) {
      return registry;
    }

    return Object.fromEntries(
      Object.entries(registry).map(([name, toolDef]) => [
        name,
        toolDef ? withToolInspector(toolDef) : toolDef,
      ]),
    ) as typeof registry;
  }, [debug]);

  return (
    <ChatRendererProvider>
      {/*
       * The inbox always shows the activity trail and the model's reasoning
       * — support wants to see what the agent did and why, regardless of the
       * chatbot's customer-facing toggles. `agentConfig` (where those
       * toggles live) is not even fetched for this view.
       */}
      <ChatRenderer
        messages={transcriptMessages}
        toolRegistry={toolRegistry}
        showToolActivity
        showReasoning
        expandToolActivity={debug ? true : chatbotConfig?.expandToolActivity}
        renderTrailStepInspector={
          debug
            ? (toolCall: ToolCall) => (
                <AdminToolCallDetails
                  toolCall={toolCall}
                  state='result'
                  content={toolCall.content}
                  artifact={toolCall.artifact}
                />
              )
            : undefined
        }
        renderSources={message => (
          <AdminSourceDetails
            message={message}
            isVoice={isVoice}
            sourcesFormat={sourcesFormat}
          />
        )}
        sourcesFormat={sourcesFormat}
        sourcesDisplayMode={sourcesDisplayMode}
        inlineCitations={inlineCitations}
        debugToolCalls={debug}
        renderUnknownTool={props =>
          props.kind === 'form' || canRenderClientSideForm(props.artifact) ? (
            renderClientSideFormFallback(props)
          ) : (
            <GenericToolCall {...props} />
          )
        }
        FeedbackComponent={AdminFeedback}
        agentName={agentName}
        agentAvatar={agentAvatar}
      />
    </ChatRendererProvider>
  );
}
